import React, { useState, useEffect } from "react";
import {
  Grid,
  TextField,
  Box,
  Divider,
  Typography,
  MenuItem,
  Snackbar,
  Alert,
  Switch,
} from "@mui/material";

import { callApi } from "../../../utils/apiClient";
import { Production_API_ENDPOINTS } from "../../../config/api/apiCallProduction";
import WasteLocationActionMenu from "./WasteLocationActionMenu";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const AddUpdateWasteLocation = ({
  formData,
  setFormData,
  setIsFormOpen,
  loadWasteLocationList,
}) => {
  const [isEdit, setIsEdit] = useState(!!formData.location_id);
  const [machineList, setMachineList] = useState([]);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("warning");

  const showAlert = (msg, severity = "warning") => {
    setAlertMessage(msg);
    setAlertSeverity(severity);
    setAlertOpen(true);
  };

  /* ===========================
     Load Machine Dropdown
     =========================== */
  useEffect(() => {
    loadMachineList();
  }, []);

  const loadMachineList = async () => {
   try {
         const companyId = Number(localStorage.getItem("companyid") || 0);
         const plantId = Number(localStorage.getItem("plantid") || 1);
         const userId = Number(localStorage.getItem("userID") || 0);
   
         const payload = { p_company_id: companyId, p_plant_id: plantId, p_created_by: userId };
         const res = await callApi(
           Production_API_ENDPOINTS.repulp.machineList || "/api/repulp/machineList",
           "POST",
           { "Content-Type": "application/json" },
           payload
         );
         setMachineList(res || []);
       } catch (err) {
         console.error("Failed to load machines", err);
         showAlert("Failed to load machine list", "error");
       }
     };
  /* ===========================
     Handlers
     =========================== */
  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateFields = () => {
    if (!formData.location_name) {
      showAlert("Location Name is required");
      return false;
    }
    if (!formData.machine_id) {
      showAlert("Machine selection is required");
      return false;
    }
    return true;
  };

  /* ===========================
     Save
     =========================== */
  const handleSave = async () => {
    if (!validateFields()) return;

    const payload = {
      p_location_id: formData.location_id || 0,
      p_location_name: formData.location_name,
      p_machine_id: formData.machine_id,
      p_companyid: localStorage.getItem("companyid"),
      p_plantid: 1,
      p_created_by: localStorage.getItem("userID"),
      p_updated_by: localStorage.getItem("userID"),
      p_status: formData.status ?? true,
    };

   const endpoint = isEdit
  ? Production_API_ENDPOINTS?.repulpmaster?.wastelocationcreate || "/api/repulpmaster/wasteLocationCreate"
  : Production_API_ENDPOINTS?.repulpmaster?.wastelocationcreate || "/api/repulpmaster/wasteLocationCreate";

try {
  const res = await callApi(
    endpoint,
    "POST",
    { "Content-Type": "application/json" },
    payload
  );
      toast.success(res?.[0]?.message || "Waste Location saved successfully", {
        position: "bottom-center",
        autoClose: 2000,
      });

      setIsEdit(true);

      if (!isEdit && res?.[0]?.p_location_id) {
        setFormData((prev) => ({
          ...prev,
          location_id: res[0].p_location_id,
        }));
      }

      setTimeout(() => loadWasteLocationList(), 500);
    } catch (error) {
      toast.error("Failed to save Waste Location", {
        position: "bottom-center",
        autoClose: 2000,
      });
    }
  };

  /* ===========================
     Delete
     =========================== */
  const handleDelete = async () => {
    if (!isEdit || !formData.location_id) return;

    try {
      const payload = {
        p_location_id: formData.location_id,
        p_company_id: localStorage.getItem("companyid"),
        p_plant_id: 1,
        p_deleted_by: localStorage.getItem("userID"),
        p_id:formData.location_id,
      };

      await callApi(
         Production_API_ENDPOINTS?.repulpmaster?.wastelocationdelete || "/api/repulpmaster/wastelocationdelete",
        "POST",
        { "Content-Type": "application/json" },
        payload
      );

      toast.success("Waste Location deleted successfully", {
        position: "bottom-center",
        autoClose: 2000,
      });

      // setTimeout(() => {
      //   loadWasteLocationList();
      //   handleClear();
      // }, 500);
    } catch (error) {
      toast.error("Error deleting Waste Location", {
        position: "bottom-center",
        autoClose: 2000,
      });
    }
  };

  /* ===========================
     Clear
     =========================== */
  const handleClear = () => {
    setFormData({
      location_id: null,
      location_name: "",
      machine_id: "",
      status: true,
    });
    setIsEdit(false);
  };

  return (
    <Box>
      <WasteLocationActionMenu
        isEdit={isEdit}
        onSave={handleSave}
        onDelete={handleDelete}
        onClear={handleClear}
        onExit={() => setIsFormOpen(false)}
        saveDisabled={!formData.location_name || !formData.machine_id}
      />

      <Divider />

      <Box p={3}>
        <Grid container spacing={2}>
          {/* Location Name */}
          <Grid item xs={8}>
            <Box display="grid" gridTemplateColumns="2fr 5fr" gap={2}>
              <Typography sx={{ color: "#fff", fontSize: 14, fontWeight: 600 }}>
                Waste Location<span style={{ color: "red" }}> *</span>
              </Typography>
              <TextField
                size="small"
                fullWidth
                value={formData.location_name || ""}
                onChange={(e) =>
                  handleChange("location_name", e.target.value)
                }
                inputProps={{ style: { color: "#fff" } }}
              />
            </Box>
          </Grid>

          {/* Machine */}
          <Grid item xs={8}>
            <Box display="grid" gridTemplateColumns="2fr 5fr" gap={2}>
              <Typography sx={{ color: "#fff", fontSize: 14, fontWeight: 600 }}>
                Machine<span style={{ color: "red" }}> *</span>
              </Typography>
              <TextField
                select
                size="small"
                fullWidth
                value={formData.machine_id || ""}
                onChange={(e) =>
                  handleChange("machine_id", e.target.value)
                }
                inputProps={{ style: { color: "#fff" } }}
              >
                {machineList.map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    {m.name}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Grid>

          {/* Status */}
          <Grid item xs={8}>
            <Box display="grid" gridTemplateColumns="2fr 5fr" gap={2}>
              <Typography sx={{ color: "#fff", fontSize: 14, fontWeight: 600 }}>
                Status
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", color: "#fff" }}>
                <Switch
                  checked={formData.status ?? true}
                  color="secondary"
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      status: e.target.checked,
                    })
                  }
                />
                <Typography>
                  {formData.status ?? true ? "Active" : "Inactive"}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Snackbar
        open={alertOpen}
        autoHideDuration={2000}
        onClose={() => setAlertOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={alertSeverity}>{alertMessage}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AddUpdateWasteLocation;
